import {
  Component,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  ModalComponent,
  ModalOptions,
  TableComponent,
} from '@shared/components';
import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { HTTPMethods, ModalTypes } from '@core/enums';
import { HttpHeaders } from '@angular/common/http';
import { LabReport } from '@core/classes/tables/lab-report';
import { TranslateService } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  TextAreaComponent,
  TextAreaOptions,
} from '@shared/components/text-area/text-area.component';
import { FILTER_TEXT_FOR_TEXTAREA } from '@core/constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'ncp-lab-report-status-action',
  imports: [ButtonComponent, ModalComponent, FormsModule, TextAreaComponent, ReactiveFormsModule],
  templateUrl: './lab-report-status-action.component.html',
  styleUrls: ['./lab-report-status-action.component.scss'],
})
export class LabReportStatusActionComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  protected translations = this.translate.instant('modal.messages');

  protected readonly apiService = inject(APIService);
  protected readonly patientService = inject(PatientService);

  @ViewChild('mod')
  public mod!: ModalComponent;

  @ViewChild('btn')
  public btn!: ButtonComponent;

  protected data = model<LabReport>();
  protected allData = model<LabReport[]>();
  protected index = input<number>();

  protected tbl = model<TableComponent<LabReport>>(new TableComponent());

  protected form: FormGroup = new FormGroup({
    reason_of_being_disabled: new FormControl<string>('', [Validators.required]),
  });

  protected reasonOptions = signal<TextAreaOptions>(
    new TextAreaOptions({
      id: 'reason_disabled',
      label: {
        text: 'reason_disabled',
      },
      name: 'reason_disabled',
      formControl: this.form.get('reason_of_being_disabled') as FormControl,
      initialValue: '',
      pattern: FILTER_TEXT_FOR_TEXTAREA,
      required: true,
    })
  );

  protected btnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'lab_orcd_action',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'lab_orcd_modal',
      type: ModalTypes.confirm,
    })
  );

  statusEffect = effect(() => {
    this.modOptions().title = this.row?.status === 1 ? 'disable_entry' : 'enable_entry';

    this.modOptions().message = this.row?.status === 1 ? 'confirm_disable' : 'confirm_enable';

    this.btnOptions().icon =
      this.row?.status === 1 ? 'bi bi-file-earmark-minus' : 'bi bi-file-earmark-plus';

    this.btnOptions().class = '';

    this.btnOptions().tooltip = this.row?.status === 1 ? 'disable_entry' : 'enable_entry';

    this.btnOptions().disabled = this.row?.status === 0;

    this.modOptions().onYes = this.changeStatus;
  });

  ngOnInit(): void {
    this.btnOptions().onClick = () => {
      this.btn.blur();
      this.mod.open();
    };

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('modal.messages');
    });
  }

  ngOnDestroy(): void {
    this.statusEffect.destroy();
  }

  get row() {
    return this.data();
  }

  set row(row) {
    this.data.set(row);
  }

  protected changeStatus = (): void => {
    const currentRow = this.data();

    if (!currentRow) {
      return;
    }

    const newStatus = currentRow.status === 1 ? 0 : 1;

    this.apiService
      .call({
        path: '/orcd-document-status',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.PUT,
        body: {
          status: newStatus,
          orcd_document_id: currentRow.id,
          documentType: currentRow.document_type,
          reason_of_being_disabled: (this.form.get('reason_of_being_disabled') as FormControl)
            .value,
        },
      })
      ?.subscribe((res: any) => {
        if (res && res.status && res.status.toString() === '1') {
          const updatedRow: LabReport = {
            ...currentRow,
            status: newStatus,
          };

          const allData = [...(this.allData() || [])];

          if (this.index() !== undefined) {
            allData[this.index()!] = updatedRow;
          }

          this.allData.set(allData);

          const table = this.tbl() as TableComponent<LabReport>;

          const tableData = [...(table.tableData || [])];

          const tableIndex = tableData.findIndex(
            (item) => item.id?.toString() === updatedRow.id?.toString()
          );

          if (tableIndex !== -1) {
            tableData[tableIndex] = updatedRow;
          }

          table.tableData = tableData;
          this.row = updatedRow;
          table.detectChanges();
          this.mod.close('success');
        }
      });
  };
}
